export type CleanActivityStatus = 'DRAFT' | 'COMPLETED' | 'APPROVED';

/** 월간 점검시트: 주별 체크 최대 개수 */
export const CLEAN_WEEK_COUNT = 5;

export const CLEAN_WEEK_LABELS = ['1주', '2주', '3주', '4주', '5주'] as const;

export interface CleanActivitySheetItem {
  id: string;
  area: string;
  checkItem: string;
  method: string;
  /** 주별 점검 결과 (길이 5) OK | NG | NA | '' */
  weekResults: string[];
  finding: string;
  remark: string;
  /** @deprecated 구 단일 결과 — 파싱 시 weekResults[0]으로 이관 */
  result?: string;
}

export interface CleanActivitySheet {
  sheetId: number;
  sheetNo?: string | null;
  title?: string | null;
  checkDate?: string | null;
  areaName?: string | null;
  checkerName?: string | null;
  itemsJson?: string | null;
  status?: string | null;
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
  approverUserId?: number | null;
  canRevoke?: boolean | null;
  approvalInProgress?: boolean | null;
  pendingApprovalRequestId?: number | null;
  canCancelApproval?: boolean | null;
  remark?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CleanActivitySheetPayload {
  sheetNo?: string;
  title?: string;
  checkDate?: string;
  areaName?: string;
  checkerName?: string;
  itemsJson?: string;
  status?: string;
  authorName?: string;
  remark?: string;
}

export interface CleanActivityEvalCriterion {
  id: string;
  criterion: string;
  maxScore: number;
  score: string;
  comment: string;
}

export interface CleanActivityEvaluation {
  evaluationId: number;
  evaluationNo?: string | null;
  title?: string | null;
  evaluationDate?: string | null;
  periodFrom?: string | null;
  periodTo?: string | null;
  targetArea?: string | null;
  evaluator?: string | null;
  criteriaJson?: string | null;
  scoreText?: string | null;
  gradeText?: string | null;
  strengths?: string | null;
  improvements?: string | null;
  overallComment?: string | null;
  status?: string | null;
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
  approverUserId?: number | null;
  canRevoke?: boolean | null;
  approvalInProgress?: boolean | null;
  pendingApprovalRequestId?: number | null;
  canCancelApproval?: boolean | null;
  remark?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CleanActivityEvaluationPayload {
  evaluationNo?: string;
  title?: string;
  evaluationDate?: string;
  periodFrom?: string;
  periodTo?: string;
  targetArea?: string;
  evaluator?: string;
  criteriaJson?: string;
  scoreText?: string;
  gradeText?: string;
  strengths?: string;
  improvements?: string;
  overallComment?: string;
  status?: string;
  authorName?: string;
  remark?: string;
}

function newId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function emptyWeekResults(): string[] {
  return Array.from({ length: CLEAN_WEEK_COUNT }, () => '');
}

export function normalizeWeekResults(
  row: Partial<CleanActivitySheetItem> & { result?: string }
): string[] {
  const raw = Array.isArray(row.weekResults) ? row.weekResults : [];
  const weeks = Array.from({ length: CLEAN_WEEK_COUNT }, (_, i) => {
    const v = raw[i];
    return v == null ? '' : String(v);
  });
  if (weeks.every((w) => !w) && row.result) {
    weeks[0] = String(row.result);
  }
  return weeks;
}

export function cleanWeekResultLabel(value?: string | null) {
  switch ((value || '').toUpperCase()) {
    case 'OK':
      return '양호';
    case 'NG':
      return '미흡';
    case 'NA':
      return '해당없음';
    default:
      return '-';
  }
}

/** 신규 등록 시 빈 양식 (항목은 사용자가 채움) */
export function defaultCleanSheetItems(): CleanActivitySheetItem[] {
  return [
    {
      id: newId(),
      area: '',
      checkItem: '',
      method: '',
      weekResults: emptyWeekResults(),
      finding: '',
      remark: '',
    },
  ];
}

export function createEmptyCleanSheetItem(): CleanActivitySheetItem {
  return {
    id: newId(),
    area: '',
    checkItem: '',
    method: '',
    weekResults: emptyWeekResults(),
    finding: '',
    remark: '',
  };
}

/** 신규 등록 시 빈 평가 항목 */
export function defaultCleanEvalCriteria(): CleanActivityEvalCriterion[] {
  return [
    {
      id: newId(),
      criterion: '',
      maxScore: 10,
      score: '',
      comment: '',
    },
  ];
}

export function parseCleanSheetItems(json?: string | null): CleanActivitySheetItem[] {
  if (!json?.trim()) return defaultCleanSheetItems();
  try {
    const parsed = JSON.parse(json) as Array<
      Partial<CleanActivitySheetItem> & { result?: string }
    >;
    if (!Array.isArray(parsed) || parsed.length === 0) return defaultCleanSheetItems();
    return parsed.map((row) => ({
      id: row.id || newId(),
      area: row.area || '',
      checkItem: row.checkItem || '',
      method: row.method || '',
      weekResults: normalizeWeekResults(row),
      finding: row.finding || '',
      remark: row.remark || '',
    }));
  } catch {
    return defaultCleanSheetItems();
  }
}

export function serializeCleanSheetItems(items: CleanActivitySheetItem[]): string {
  return JSON.stringify(
    items.map((row) => ({
      id: row.id,
      area: row.area,
      checkItem: row.checkItem,
      method: row.method,
      weekResults: normalizeWeekResults(row),
      finding: row.finding,
      remark: row.remark,
    }))
  );
}

export function parseCleanEvalCriteria(json?: string | null): CleanActivityEvalCriterion[] {
  if (!json?.trim()) return defaultCleanEvalCriteria();
  try {
    const parsed = JSON.parse(json) as CleanActivityEvalCriterion[];
    if (!Array.isArray(parsed) || parsed.length === 0) return defaultCleanEvalCriteria();
    return parsed.map((row) => ({
      id: row.id || newId(),
      criterion: row.criterion || '',
      maxScore: row.maxScore ?? 10,
      score: row.score || '',
      comment: row.comment || '',
    }));
  } catch {
    return defaultCleanEvalCriteria();
  }
}

/** 이전 점검시트에서 구역·점검내용·방법만 재사용 (결과·발견사항은 비움) */
export function mapSheetItemsForReuse(
  items: CleanActivitySheetItem[]
): CleanActivitySheetItem[] {
  const mapped = items.map((row) => ({
    id: newId(),
    area: row.area || '',
    checkItem: row.checkItem || '',
    method: row.method || '',
    weekResults: emptyWeekResults(),
    finding: '',
    remark: '',
  }));
  return mapped.length > 0 ? mapped : defaultCleanSheetItems();
}

/** 점검시트 항목 → 평가항목 (점검내용·방법을 평가항목·의견으로) */
export function mapSheetItemsToEvalCriteria(
  items: CleanActivitySheetItem[]
): CleanActivityEvalCriterion[] {
  const mapped = items
    .map((row) => {
      const criterion = [row.area, row.checkItem].filter((v) => Boolean(v?.trim())).join(' · ');
      return {
        id: newId(),
        criterion,
        maxScore: 10,
        score: '',
        comment: row.method || '',
      };
    })
    .filter((row) => row.criterion || row.comment);
  return mapped.length > 0 ? mapped : defaultCleanEvalCriteria();
}

export function cleanStatusLabel(
  status?: string | null,
  approvalInProgress?: boolean | null
) {
  if (approvalInProgress) return '결재 요청 중';
  switch ((status || '').toUpperCase()) {
    case 'APPROVED':
      return '승인완료';
    case 'COMPLETED':
      return '작성완료';
    default:
      return '작성중';
  }
}

export function calcCleanEvalScore(criteria: CleanActivityEvalCriterion[]) {
  let sum = 0;
  let max = 0;
  for (const row of criteria) {
    const s = Number(row.score);
    if (!Number.isNaN(s) && row.score !== '') sum += s;
    max += row.maxScore || 0;
  }
  return { sum, max };
}

export function cleanGradeFromScore(sum: number, max: number) {
  if (max <= 0) return '';
  const pct = (sum / max) * 100;
  if (pct >= 90) return '우수';
  if (pct >= 80) return '양호';
  if (pct >= 70) return '보통';
  if (pct >= 60) return '미흡';
  return '불량';
}
